import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Info, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

const PARTICIPANT_INCLUDED = [
  "Free account & unlimited messaging",
  "Verified, screened support workers",
  "Free worker matching by suburb & service",
  "Auto-generated NDIS-aligned invoices",
  "24-hour cancellation window with zero fee",
  "Australian-based support team"
];
const PARTICIPANT_EXCLUDED = [
  "Sign-up fees", "Booking fees", "Cancellation fees (24+ hours notice)", "Subscription fees"
];

const RATE_TABLE = [
  { service: "Personal Care", weekday: "55", evening: "60", weekend: "65", public: "75" },
  { service: "Community Access", weekday: "55", evening: "60", weekend: "65", public: "75" },
  { service: "Domestic Assistance", weekday: "50", evening: "55", weekend: "60", public: "70" },
  { service: "Transport (per hr)", weekday: "55", evening: "60", weekend: "65", public: "75" },
  { service: "Therapy Support", weekday: "70", evening: "75", weekend: "80", public: "90" },
  { service: "Nursing Support", weekday: "85", evening: "92", weekend: "100", public: "115" }
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="container mx-auto px-4 py-5 flex items-center justify-between max-w-6xl">
        <Link to="/" className="font-bold text-xl text-slate-900">BTR Health</Link>
        <div className="flex gap-3">
          <Link to="/"><Button variant="outline" className="rounded-full">Home</Button></Link>
          <Link to="/register"><Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full">Get started</Button></Link>
        </div>
      </header>

      <section className="container mx-auto px-4 py-14 max-w-4xl text-center">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-6xl font-bold text-slate-900 mb-5 leading-tight">
          Honest pricing.<br /><span className="text-[#87CEEB]">No surprises.</span>
        </motion.h1>
        <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Participants pay zero platform fees. Workers keep more of what they earn. Rates are aligned with NDIS Price Guide caps.
        </p>
      </section>

      <section className="container mx-auto px-4 max-w-4xl mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-7">
            <h3 className="text-xl font-bold text-emerald-900 mb-3">For Participants</h3>
            <p className="text-3xl font-bold text-emerald-900 mb-1">$0<span className="text-base font-normal text-emerald-700">/forever</span></p>
            <p className="text-sm text-emerald-700 mb-5">No sign-up. No booking. No subscription. No nonsense.</p>
            <ul className="space-y-2 text-sm">
              {PARTICIPANT_INCLUDED.map(item => (
                <li key={item} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span className="text-emerald-900">{item}</span>
                </li>
              ))}
              {PARTICIPANT_EXCLUDED.map(item => (
                <li key={item} className="flex items-start gap-2">
                  <X className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                  <span className="text-emerald-900 line-through opacity-60">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-sky-50 border border-sky-200 rounded-2xl p-7">
            <h3 className="text-xl font-bold text-sky-900 mb-3">For Independent Workers</h3>
            <p className="text-3xl font-bold text-sky-900 mb-1">10%<span className="text-base font-normal text-sky-700"> platform fee</span></p>
            <p className="text-sm text-sky-700 mb-5">You keep 90% of every shift. We handle invoicing, NDIS routing, and admin.</p>
            <ul className="space-y-2 text-sm">
              {[
                "Set your own hourly rate",
                "Public Liability insurance available",
                "Auto-generated invoices and remittance",
                "Payments within 5 business days",
                "No exclusivity — work elsewhere too"
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sky-900">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-2">Indicative Hourly Rates</h2>
          <p className="text-center text-slate-500 mb-8 text-sm">Capped at NDIS Price Guide. Actual rate is set by each worker on their profile.</p>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-100">
            <table className="w-full text-sm" data-testid="pricing-rate-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-4">Support Type</th>
                  <th className="p-4 text-right">Weekday</th>
                  <th className="p-4 text-right">Evening</th>
                  <th className="p-4 text-right">Weekend</th>
                  <th className="p-4 text-right">Public Holiday</th>
                </tr>
              </thead>
              <tbody>
                {RATE_TABLE.map(r => (
                  <tr key={r.service} className="border-t border-slate-100">
                    <td className="p-4 font-medium text-slate-900">{r.service}</td>
                    <td className="p-4 text-right">${r.weekday}</td>
                    <td className="p-4 text-right">${r.evening}</td>
                    <td className="p-4 text-right">${r.weekend}</td>
                    <td className="p-4 text-right">${r.public}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              All NDIS supports are <strong>GST-free</strong>. Invoices include a customisable NDIS line item code so you can submit them straight to your plan manager or upload to MyPlace.
            </p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 max-w-3xl text-center">
        <h2 className="text-3xl font-bold text-slate-900 mb-4">Got questions about pricing?</h2>
        <p className="text-slate-600 mb-6">Our help center covers cancellations, weekend rates, evenings and public holidays.</p>
        <Link to="/help"><Button size="lg" variant="outline" className="rounded-full px-8 h-12">Visit help center<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
      </section>
      <Footer />
    </div>
  );
}
